import { useUserStore } from "~/store/UserStore";

/**
 * Boots the SignalR notifications connection on the client.
 *
 *  - Connects immediately (anonymously) so even visitors who are NOT logged in receive
 *    site-wide broadcast notifications.
 *  - Watches the user store and, once a CLD user id is available, subscribes that connection
 *    to its per-user group so the user also receives notifications targeted at selected ids.
 *    On logout it unsubscribes again.
 *
 * The id used here (cldUser.id) is what the back-end must address when calling
 * INotificationService.SendToUsersAsync([...]) — keep the two in sync.
 */
export default defineNuxtPlugin(async () => {
  // Disabled unless NOTIFICATIONS_ENABLED=true — see runtimeConfig.public.notificationsEnabled.
  if (!useRuntimeConfig().public.notificationsEnabled) return;

  // Imported dynamically, not at module scope: Nuxt bundles plugins into the entry
  // chunk, so a static import put all of @microsoft/signalr on the critical path of
  // every page — even though this flag is off by default. Behind the await it lands
  // in its own chunk that is only fetched when notifications are actually enabled.
  const { useNotifications } = await import("~/composables/useNotifications");

  const { connect, subscribe, unsubscribe, loadHistory, resetLocal } = useNotifications();

  // 0) Hydrate the user's local notification preferences (mute / pop-up alerts / broadcasts)
  //    from localStorage so the incoming handler respects them from the first notification on.
  useNotificationSettings().load();

  // 1) Anonymous connection for broadcasts.
  connect();

  // 2) Follow login state for user-targeted notifications.
  const userStore = useUserStore();
  let lastUserId: string | null = null;

  watch(
    () => userStore.cldUser?.id,
    async (userId) => {
      const id = userId ? String(userId) : "";
      if (id && id !== lastUserId) {
        if (lastUserId) await unsubscribe(lastUserId);
        lastUserId = id;
        await subscribe(id);
        // Seed the bell from the DB.
        await loadHistory();
      } else if (!id && lastUserId) {
        await unsubscribe(lastUserId);
        lastUserId = null;
        // Only empty the bell on a genuine logout. During a page refresh cldUser can momentarily
        // reset to empty before being restored.
        // resetLocal, NOT clear: clear() is the "delete all" action and asks the server to delete
        // the customer's notifications. Called here it raced the logout request, and whenever
        // it arrived first the customer lost their notifications by logging out.
        if (!userStore.isAuthenticated) resetLocal();
      }
    },
    { immediate: true }
  );
});
